<script setup lang="ts">
import type { Leg, TripType } from '@optionway/models';
import { ArrowRight, CircleAlert, Clock, Plane, PlaneLanding, PlaneTakeoff } from 'lucide-vue-next';

const prefix = 'components.results.flightDetailsDialog';

const properties = defineProps<{
  legs: Leg[];
  tripType?: TripType;
  initialLegId?: number;
}>();

const { locale } = useI18n();
const locationLabelsStore = useLocationLabels();

const legReferences: Partial<Record<number, Element>> = {};

const setLegReference = (element: unknown, legId?: number) => {
  if (legId !== undefined && element instanceof Element) {
    legReferences[legId] = element;
  }
};

// The dialog remounts this component on every opening; nextTick lets its open-autofocus run first
// so it cannot undo the scroll.
onMounted(async () => {
  if (properties.initialLegId === undefined) return;
  await nextTick();
  legReferences[properties.initialLegId]?.scrollIntoView({ block: 'start' });
});
</script>

<template>
  <div
    class="flex flex-col gap-6"
    :class="tripType === 'ROUND_TRIP' ? 'lg:grid lg:grid-cols-2 lg:items-start' : ''"
  >
    <template
      v-for="(leg, legIndex) in properties.legs"
      :key="leg.id"
    >
      <div
        :ref="(element) => setLegReference(element, leg.id)"
        class="flex flex-col gap-4 lg:gap-0 lg:rounded-lg lg:border lg:border-gray-300"
      >
        <!-- Leg header -->
        <div
          class="flex flex-wrap items-center gap-3 border-b border-gray-300 pb-3 lg:overflow-hidden lg:rounded-t-lg lg:border-b-0 lg:bg-gray-100 lg:px-3 lg:pt-3"
        >
          <UiCustomBadge
            variant="purple"
            radius="m"
            size="s"
            class="gap-1.5 self-start lg:self-auto"
          >
            <template v-if="tripType === 'MULTI_DEST'">
              <Plane
                class="h-4 w-4"
                aria-hidden="true"
              />
              <h3 class="font-semibold uppercase tracking-wide">{{ $t(`${prefix}.leg`) }} {{ legIndex + 1 }}</h3>
            </template>
            <template v-else>
              <component
                :is="legIndex === 0 ? PlaneTakeoff : PlaneLanding"
                class="h-4 w-4"
                aria-hidden="true"
              />
              <h3 class="font-semibold uppercase tracking-wide">
                {{ legIndex === 0 ? $t(`${prefix}.outbound`) : $t(`${prefix}.inbound`) }}
              </h3>
            </template>
          </UiCustomBadge>
          <div class="flex flex-wrap items-center gap-x-1 text-sm font-semibold text-foreground">
            <span>{{ locationLabelsStore.city(leg.departure?.cityIataCode) }}</span>
            <ArrowRight
              class="h-3.5 w-3.5 shrink-0"
              aria-hidden="true"
            />
            <span>{{ locationLabelsStore.city(leg.arrival?.cityIataCode) }}</span>
            <span>· {{ useDateFormat(leg.departureDateTime, 'ddd DD/MM', { locales: locale }).value }}</span>
          </div>
          <UiCustomBadge
            :variant="displayTypeToVariant(leg.stopover?.displayType)"
            size="s"
          >
            <CircleAlert
              v-if="leg.stopover?.displayType === 'WARNING'"
              :size="12"
              aria-hidden="true"
            />
            <span v-if="leg.stopover?.count">
              {{ $t('components.results.leg.stopover', leg.stopover.count) }}
            </span>
            <span v-else>{{ $t('components.results.leg.stopover', 0) }}</span>
          </UiCustomBadge>
        </div>

        <!-- Segments -->
        <div
          v-for="(segment, segIndex) in leg.segments"
          :key="`${segment.flightNumber?.iataCode}-${segment.flightNumber?.number}`"
          class="flex flex-col items-stretch justify-center gap-3 self-stretch font-normal lg:p-3"
          :class="segIndex < leg.segments.length - 1 ? 'lg:pb-0' : ''"
        >
          <ResultsSegment v-bind="segment" />
          <ResultsBottomCorrespondence
            v-if="segIndex < leg.segments.length - 1 && leg.segments[segIndex + 1]?.previousCorrespondence"
            v-bind="leg.segments[segIndex + 1]?.previousCorrespondence"
          />
        </div>

        <!-- Total duration (multi-segment legs only) -->
        <template v-if="leg.segments.length > 1">
          <div class="border-t border-gray-300 lg:mx-3" />
          <div class="flex items-center justify-between py-2.5 lg:px-3">
            <span class="text-sm text-text-secondary-600">
              {{ $t(`${prefix}.totalDuration`) }}
            </span>
            <span class="inline-flex items-center gap-1 text-sm font-medium">
              <Clock
                :size="12"
                class="shrink-0 text-text-secondary-600"
                aria-hidden="true"
              />
              {{ useDurationFormat(leg.duration) }}
            </span>
          </div>
        </template>
      </div>

      <!-- Leg separator (mobile only) -->
      <div
        v-if="legIndex < properties.legs.length - 1"
        class="border-t border-gray-300 lg:hidden"
      />
    </template>
  </div>
</template>
